'use client'

import { useState } from 'react'
import Link from 'next/link'
import { LandingNav } from '@/components/marketing/LandingNav'
import { MarketingFooter } from '@/components/marketing/MarketingFooter'

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle')
  const [form, setForm] = useState({ name: '', email: '', message: '' })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setStatus('loading')
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!res.ok) throw new Error()
      setStatus('sent')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <LandingNav />

      {/* Hero */}
      <section className="bg-chalkboard pt-32 pb-20 px-4 text-center">
        <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight mb-4">Get in touch.</h1>
        <p className="text-white/60 text-lg">We're a small team. Real people reply.</p>
      </section>

      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-2 gap-12">

          {/* Contact options */}
          <div>
            <div className="mb-8 p-6 bg-slate-50 rounded-2xl">
              <h3 className="font-black text-chalk-navy text-lg mb-2">For coaches</h3>
              <p className="text-sm text-slate-500 mb-3">Questions about using Chalk for your team? We reply same day.</p>
              <a href="mailto:hello@chalksports.ai" className="text-chalk-navy font-semibold underline text-sm">
                hello@chalksports.ai
              </a>
            </div>
            <div className="p-6 bg-slate-50 rounded-2xl">
              <h3 className="font-black text-chalk-navy text-lg mb-2">Press & partnerships</h3>
              <p className="text-sm text-slate-500 mb-3">Media inquiries, partnership opportunities, and everything else.</p>
              <a href="mailto:jared@chalksports.ai" className="text-chalk-navy font-semibold underline text-sm">
                jared@chalksports.ai
              </a>
            </div>
          </div>

          {/* Contact form */}
          <div>
            {status === 'sent' ? (
              <div className="text-center py-16">
                <div className="text-5xl mb-4">✅</div>
                <h3 className="font-black text-chalk-navy text-xl mb-2">Message sent!</h3>
                <p className="text-slate-500 text-sm">We'll get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Name</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-chalk-yellow"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Email</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-chalk-yellow"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Message</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-chalk-yellow resize-none"
                    placeholder="What's on your mind?"
                  />
                </div>
                {status === 'error' && (
                  <p className="text-sm text-red-500">Something went wrong. Email us directly at hello@chalksports.ai</p>
                )}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full bg-chalk-yellow text-chalk-navy font-bold py-3 rounded-xl hover:bg-chalk-yellow/90 transition-colors disabled:opacity-50"
                >
                  {status === 'loading' ? 'Sending...' : 'Send message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
